import { Link } from "react-router-dom";
import { useState, useEffect } from "react";

const qrTypes = ["URL", "vCard", "WiFi", "PDF", "Instagram", "WhatsApp"];

export default function HeroSection() {
  const [activeType, setActiveType] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveType((prev) => (prev + 1) % qrTypes.length);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-br from-slate-50 via-white to-blue-50/30 pt-16">
      {/* Background decorations */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-20 right-10 w-72 h-72 bg-blue-100/40 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-sky-100/30 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-20 w-full">
        <div className="flex flex-col lg:flex-row items-center gap-16">
          {/* Left Content */}
          <div className="flex-1 text-center lg:text-left">
            <div className="inline-flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-full px-4 py-1.5 mb-6">
              <span className="w-2 h-2 bg-blue-600 rounded-full animate-pulse"></span>
              <span className="text-blue-700 text-xs font-semibold tracking-wide">Dinamik QR Kod Çözümü</span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6">
              QR Kodlarınızı{" "}
              <span className="text-blue-600">Oluşturun</span>,{" "}
              <br className="hidden lg:block" />
              Yönetin &amp; Takip Edin
            </h1>

            <p className="text-lg text-slate-500 leading-relaxed mb-8 max-w-xl mx-auto lg:mx-0">
              PNG, SVG ve PDF formatlarında indirin. Gerçek zamanlı tarama istatistikleriyle hangi ülkeden, ne zaman tarandığını görün.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <Link
                to="/register"
                className="inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-full transition-all cursor-pointer whitespace-nowrap"
              >
                Ücretsiz Başla
                <i className="ri-arrow-right-line"></i>
              </Link>
              <Link
                to="/pricing"
                className="inline-flex items-center justify-center gap-2 px-8 py-3.5 border-2 border-slate-200 hover:border-blue-300 text-slate-700 font-semibold rounded-full transition-all cursor-pointer whitespace-nowrap"
              >
                Planları Gör
              </Link>
            </div>

            {/* Stats */}
            <div className="flex flex-wrap gap-8 mt-12 justify-center lg:justify-start">
              {[
                { value: "2M+", label: "QR Kod Oluşturuldu" },
                { value: "150+", label: "Ülkede Kullanılıyor" },
                { value: "99.9%", label: "Çalışma Süresi" },
              ].map((stat) => (
                <div key={stat.label}>
                  <div className="text-2xl font-bold text-slate-900">{stat.value}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Visual */}
          <div className="flex-1 flex justify-center lg:justify-end">
            <div className="relative w-full max-w-md">
              {/* Main Card */}
              <div className="bg-white rounded-3xl p-8 border border-slate-100">
                <div className="flex items-center justify-between mb-6">
                  <span className="text-sm font-semibold text-slate-700">QR Kod Önizleme</span>
                  <span className="text-xs bg-blue-100 text-blue-700 px-2.5 py-1 rounded-full font-medium">
                    {qrTypes[activeType]}
                  </span>
                </div>

                {/* QR Code Visual */}
                <div className="flex justify-center mb-6">
                  <div className="w-48 h-48 bg-slate-50 rounded-2xl flex items-center justify-center border-2 border-dashed border-slate-200 relative overflow-hidden">
                    <div className="grid grid-cols-7 gap-1 p-3">
                      {Array.from({ length: 49 }).map((_, i) => (
                        <div
                          key={i}
                          className={`w-4 h-4 rounded-sm transition-all duration-300 ${
                            [0,1,2,3,4,5,6,7,13,14,20,21,27,28,34,35,41,42,43,44,45,46,48,10,11,12,15,16,17,18,19,22,23,24,25,26,29,30,31,32,33,36,37,38,39,40,47].includes(i)
                              ? "bg-slate-900"
                              : "bg-transparent"
                          }`}
                        ></div>
                      ))}
                    </div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center border border-slate-100">
                        <img
                          src="https://public.readdy.ai/ai/img_res/5f29dc48-9590-4b13-ae7e-f0353c3fcc43.png"
                          alt="logo"
                          className="w-7 h-7 object-contain"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Format Badges */}
                <div className="flex justify-center gap-3 mb-6">
                  {["PNG", "SVG", "PDF"].map((fmt) => (
                    <span
                      key={fmt}
                      className="px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs font-semibold text-slate-600"
                    >
                      {fmt}
                    </span>
                  ))}
                </div>

                <Link
                  to="/register"
                  className="w-full flex items-center justify-center gap-2 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition-all cursor-pointer"
                >
                  <i className="ri-qr-code-line"></i>
                  QR Kod Oluştur
                </Link>
              </div>

              {/* Floating badges */}
              <div className="absolute -top-4 -right-4 bg-white border border-slate-100 rounded-2xl px-4 py-2.5 flex items-center gap-2">
                <div className="w-8 h-8 flex items-center justify-center bg-blue-100 rounded-full">
                  <i className="ri-bar-chart-line text-blue-600 text-sm"></i>
                </div>
                <div>
                  <div className="text-xs font-bold text-slate-900">48.3K</div>
                  <div className="text-xs text-slate-400">Tarama</div>
                </div>
              </div>

              <div className="absolute -bottom-4 -left-4 bg-white border border-slate-100 rounded-2xl px-4 py-2.5 flex items-center gap-2">
                <div className="w-8 h-8 flex items-center justify-center bg-sky-100 rounded-full">
                  <i className="ri-global-line text-blue-600 text-sm"></i>
                </div>
                <div>
                  <div className="text-xs font-bold text-slate-900">150+ Ülke</div>
                  <div className="text-xs text-slate-400">Erişim</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}